import React, { useState, useEffect, useMemo } from 'react';
import { apiClient } from '../apiClient';
import { deriveCategoriesFromExercises } from '../utils/categoryUtils';
import { sortCategoriesWithPredefined } from '../utils/categoryOrdering';

const CategorySidebar = ({ onCategorySelect, selectedCategory }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  const [exercises, setExercises] = useState([]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load exercises from the exercises table
      const exercisesData = await apiClient.getExercises();
      setExercises(exercisesData);
    } catch (err) {
      setError('Failed to load categories');
      console.error('Error loading categories:', err);
    } finally {
      setLoading(false);
    }
  };

  // Memoize category derivation for performance optimization
  const derivedCategories = useMemo(() => {
    if (!exercises || exercises.length === 0) {
      return [];
    }
    return deriveCategoriesFromExercises(exercises);
  }, [exercises]);

  const handleCategoryClick = (categoryId) => {
    if (onCategorySelect) {
      onCategorySelect(categoryId);
    }
  };

  const toggleCollapse = () => {
    const newCollapsed = !collapsed;
    setCollapsed(newCollapsed);
    // No persistence - collapse state resets on page refresh per requirements
  };

  if (loading) {
    return (
      <aside
        data-testid="category-sidebar"
        role="navigation"
        aria-label="Exercise categories"
        aria-busy="true"
        style={{
          width: '250px',
          minHeight: 'calc(100vh - 40px)',
          borderRight: '1px solid var(--border-color, #e5e5e5)',
          backgroundColor: 'var(--sidebar-bg, #f8f9fa)',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <div style={{
          fontSize: '24px',
          marginBottom: '16px',
          animation: 'spin 1s linear infinite'
        }}>
          ⟳
        </div>
        <div
          role="status"
          aria-live="polite"
          style={{
            color: 'var(--text-secondary, #666)',
            fontSize: '14px',
            textAlign: 'center'
          }}
        >
          Loading categories...
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </aside>
    );
  }

  if (error) {
    return (
      <aside
        data-testid="category-sidebar"
        role="navigation"
        aria-label="Exercise categories"
        style={{
          width: '250px',
          minHeight: 'calc(100vh - 40px)',
          borderRight: '1px solid var(--border-color, #e5e5e5)',
          backgroundColor: 'var(--sidebar-bg, #f8f9fa)',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <div style={{
          fontSize: '32px',
          marginBottom: '16px',
          opacity: 0.6
        }}>
          ⚠️
        </div>
        <div
          role="alert"
          aria-live="assertive"
          style={{
            color: '#dc3545',
            fontSize: '14px',
            textAlign: 'center',
            marginBottom: '16px',
            lineHeight: '1.4'
          }}
        >
          {error}
        </div>
        <button
          aria-label="Retry loading categories"
          onClick={loadCategories}
          style={{
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'background-color 0.2s ease'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#c82333'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#dc3545'}
        >
          🔄 Retry
        </button>
      </aside>
    );
  }

  return (
    <aside
      data-testid="category-sidebar"
      role="navigation"
      aria-label="Exercise categories"
      aria-expanded={!collapsed}
      style={{
        width: collapsed ? '60px' : '250px',
        height: '100vh',
        borderRight: '1px solid var(--border-color, #e5e5e5)',
        backgroundColor: 'var(--sidebar-bg, #f8f9fa)',
        transition: 'width 0.3s ease',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Sidebar Header */}
      <div style={{
        padding: '20px',
        borderBottom: '1px solid var(--border-color, #e5e5e5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        {!collapsed && (
          <h3
            id="categories-heading"
            style={{
              margin: 0,
              color: 'var(--text-color, #333)',
              fontSize: '18px',
              fontWeight: '600'
            }}
          >
            Categories
          </h3>
        )}
        <button
          data-testid="sidebar-toggle"
          aria-label={collapsed ? 'Expand category sidebar' : 'Collapse category sidebar'}
          aria-expanded={!collapsed}
          aria-controls="categories-heading"
          onClick={toggleCollapse}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '4px',
            color: 'var(--text-color, #666)',
            fontSize: '16px'
          }}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? '→' : '←'}
        </button>
      </div>

      {/* Category Navigation */}
      <nav
        role="menu"
        aria-labelledby="categories-heading"
        style={{
          flex: 1,
          padding: collapsed ? '10px 5px' : '20px',
          overflow: 'auto'
        }}
      >
        {derivedCategories.length === 0 ? (
          // Empty state when no categories are available
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: 'var(--text-secondary, #666)'
          }}>
            {!collapsed && (
              <>
                <div style={{
                  fontSize: '48px',
                  marginBottom: '16px',
                  opacity: 0.5
                }}>
                  📂
                </div>
                <h4 style={{
                  margin: '0 0 8px 0',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--text-color, #333)'
                }}>
                  No Categories Available
                </h4>
                <p style={{
                  margin: 0,
                  fontSize: '14px',
                  lineHeight: '1.4'
                }}>
                  Categories will appear here when exercises are loaded.
                </p>
              </>
            )}
          </div>
        ) : (
          <ul
            role="menubar"
            aria-orientation="vertical"
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '4px'
            }}
          >
            {derivedCategories.map((category) => (
            <li key={category.name} role="none">
              <button
                data-testid={`category-${category.name}`}
                role="menuitem"
                aria-current={selectedCategory === category.name ? 'true' : 'false'}
                aria-label={`Filter exercises by ${category.name}. ${category.exercise_count} exercises available.`}
                onClick={() => handleCategoryClick(category.name)}
                style={{
                  width: '100%',
                  padding: collapsed ? '12px 8px' : '12px 16px',
                  border: selectedCategory === category.name
                    ? '1px solid var(--color-border, #e0e0e0)'
                    : '1px solid transparent',
                  borderRadius: '8px',
                  backgroundColor: selectedCategory === category.name
                    ? 'var(--color-surface, #ffffff)'
                    : 'transparent',
                  color: selectedCategory === category.name
                    ? 'var(--color-primary, #1976d2)'
                    : 'var(--color-surface-text, #1c1b1f)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: selectedCategory === category.name ? '600' : '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: collapsed ? '0' : '12px',
                  outline: 'none',
                  boxShadow: selectedCategory === category.name
                    ? '0 1px 3px var(--color-shadow, rgba(0, 0, 0, 0.1))'
                    : 'none',
                  transform: selectedCategory === category.name
                    ? 'translateX(1px)'
                    : 'translateX(0)',
                  position: 'relative',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  MozUserSelect: 'none',
                  msUserSelect: 'none',
                  WebkitTouchCallout: 'none',
                  WebkitTapHighlightColor: 'transparent',
                  textShadow: 'none'
                }}
                className={selectedCategory === category.name ? 'selected' : ''}
                aria-pressed={selectedCategory === category.name}
                onMouseOver={(e) => {
                  if (selectedCategory !== category.name) {
                    e.target.style.backgroundColor = '#f1f1f1';
                  }
                }}
                onMouseOut={(e) => {
                  if (selectedCategory !== category.name) {
                    e.target.style.backgroundColor = 'transparent';
                  }
                }}
                onFocus={(e) => {
                  e.target.style.outline = '2px solid var(--accent-color, #007bff)';
                  e.target.style.outlineOffset = '2px';
                }}
                onBlur={(e) => {
                  e.target.style.outline = 'none';
                }}
              >
                {/* Category Icon/Emoji based on category type */}
                <span style={{
                  fontSize: '16px',
                  minWidth: '20px',
                  textAlign: 'center',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  MozUserSelect: 'none',
                  msUserSelect: 'none',
                  pointerEvents: 'none'
                }}>
                  {getCategoryIcon(category.name)}
                </span>

                {!collapsed && (
                  <div style={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    overflow: 'hidden',
                    pointerEvents: 'none'
                  }}>
                    <span style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      MozUserSelect: 'none',
                      msUserSelect: 'none',
                      pointerEvents: 'none'
                    }}>
                      {category.name}
                    </span>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      pointerEvents: 'none'
                    }}>
                      <span style={{
                        fontSize: '12px',
                        opacity: 0.7,
                        flexShrink: 0,
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        MozUserSelect: 'none',
                        msUserSelect: 'none',
                        pointerEvents: 'none'
                      }}>
                        {category.exercise_count}
                      </span>
                      {selectedCategory === category.name && (
                        <span style={{
                          fontSize: '12px',
                          opacity: 0.8,
                          flexShrink: 0,
                          userSelect: 'none',
                          WebkitUserSelect: 'none',
                          MozUserSelect: 'none',
                          msUserSelect: 'none',
                          pointerEvents: 'none'
                        }}>
                          →
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </button>
            </li>
          ))}
          </ul>
        )}
      </nav>

      {/* Footer with category count */}
      {!collapsed && (
        <div style={{
          padding: '15px 20px',
          borderTop: '1px solid var(--border-color, #e5e5e5)',
          fontSize: '12px',
          color: 'var(--text-secondary, #666)',
          textAlign: 'center'
        }}>
          {derivedCategories.length} {derivedCategories.length === 1 ? 'category' : 'categories'}
        </div>
      )}
    </aside>
  );
};

// Helper function to get category icons
const getCategoryIcon = (categoryName) => {
  const icons = {
    'all categories': '📚',
    'python': '🐍',
    'linear algebra': '📊',
    'machine learning': '🤖',
    'deep learning': '🧠',
    'nlp': '💬',
    'mathematics': '🔢'
  };
  return icons[categoryName?.toLowerCase()] || '📁';
};

export default CategorySidebar;